<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary btn-sm" @click="goBackToList">
          <i class="ri-arrow-left-line"></i> 返回
        </button>
        <button type="button" class="btn btn-secondary btn-sm" @click="handleReset" :disabled="submitting">
          重置
        </button>
        <button type="button" class="btn btn-primary btn-sm" @click="handleSubmit" :disabled="submitting">
          <span v-if="submitting"><i class="ri-loader-4-line spin"></i> 儲存中...</span>
          <span v-else><i class="ri-save-line"></i> 儲存設定</span>
        </button>
      </div>
    </div>

    <div class="page-container">

    <!-- Tab 導航 -->
    <div class="tab-nav">
      <button
        type="button"
        class="tab-btn"
        :class="{ active: activeTab === 'basic' }"
        @click="activeTab = 'basic'"
      >
        <i class="ri-settings-3-line"></i> 基本設定
      </button>
      <button
        type="button"
        class="tab-btn"
        :class="{ active: activeTab === 'rewards' }"
        @click="activeTab = 'rewards'"
      >
        <i class="ri-gift-line"></i> 點數 &amp; 優惠券
      </button>
      <button
        type="button"
        class="tab-btn"
        :class="{ active: activeTab === 'notification' }"
        @click="activeTab = 'notification'"
      >
        <i class="ri-notification-3-line"></i> 通知設定
      </button>
      <button
        type="button"
        class="tab-btn"
        :class="{ active: activeTab === 'records' }"
        @click="activeTab = 'records'"
      >
        <i class="ri-list-check"></i> 發送紀錄
      </button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <template v-else>
      <!-- ======== Tab 1：基本設定 ======== -->
      <div v-show="activeTab === 'basic'" class="card">
        <div class="card-body">
          <form @submit.prevent="handleSubmit">
            <!-- 基本設定 -->
            <div class="form-section">
            <h2 class="section-title">基本設定</h2>

            <div class="form-group">
              <label class="form-label">模組名稱 <span class="required">*</span></label>
              <input
                v-model="form.moduleName"
                type="text"
                class="form-control"
                maxlength="100"
                placeholder="例如：2026 週年禮"
              />
              <span v-if="errors.moduleName" class="error-text">{{ errors.moduleName }}</span>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label class="form-label">啟用日期起 <span class="required">*</span></label>
                <input
                  v-model="form.activeStartAtLocal"
                  type="date"
                  class="form-control"
                />
                <span v-if="errors.activeStartAtLocal" class="error-text">{{ errors.activeStartAtLocal }}</span>
              </div>
              <div class="form-group">
                <label class="form-label">啟用日期迄</label>
                <input
                  v-model="form.activeEndAtLocal"
                  type="date"
                  class="form-control"
                />
                <small class="form-text">留空表示無結束時間</small>
              </div>
            </div>
            
            <div class="form-group">
              <div class="toggle-row">
                <label class="toggle-label">啟用週年禮自動發送</label>
                <label class="toggle-switch">
                  <input v-model="form.isEnabled" type="checkbox" />
                  <span class="toggle-slider"></span>
                </label>
                <span class="toggle-status" :class="form.isEnabled ? 'on' : 'off'">
                  {{ form.isEnabled ? '已啟用' : '已停用' }}
                </span>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">每日發送時間 <span class="required">*</span></label>
              <div class="hour-grid" :class="{ 'is-invalid': errors.dailySendTime }">
                <button
                  v-for="h in 24"
                  :key="h - 1"
                  type="button"
                  class="hour-btn"
                  :class="{ active: form.dailySendTime === `${String(h - 1).padStart(2, '0')}:00` }"
                  @click="form.dailySendTime = `${String(h - 1).padStart(2, '0')}:00`"
                >
                  {{ String(h - 1).padStart(2, '0') }}:00
                </button>
              </div>
              <small class="form-text">點選發送時間，目前選擇：<strong>{{ form.dailySendTime }}</strong></small>
              <span v-if="errors.dailySendTime" class="error-text">{{ errors.dailySendTime }}</span>
            </div>

            <div class="form-group">
              <label class="form-label">發送時機 <span class="required">*</span></label>
              <div class="timing-cards" :class="{ 'is-invalid': errors.sendTiming }">
                <label
                  class="timing-card"
                  :class="{ active: form.sendTiming === 'anniversary_day' }"
                >
                  <input type="radio" v-model="form.sendTiming" value="anniversary_day" />
                  <i class="ri-calendar-event-line timing-icon"></i>
                  <div class="timing-label">週年當日</div>
                  <div class="timing-desc">在會員入會滿週年當天發送（2/29 註冊者平年於 2/28 發送）</div>
                </label>
                <label
                  class="timing-card"
                  :class="{ active: form.sendTiming === 'days_before' }"
                >
                  <input type="radio" v-model="form.sendTiming" value="days_before" />
                  <i class="ri-timer-line timing-icon"></i>
                  <div class="timing-label">週年日前 N 天</div>
                  <div class="timing-desc">提前幾天發送週年禮</div>
                </label>
              </div>
              <span v-if="errors.sendTiming" class="error-text">{{ errors.sendTiming }}</span>
            </div>

            <div v-if="form.sendTiming === 'days_before'" class="form-group">
              <label class="form-label">提前天數 <span class="required">*</span></label>
              <div class="days-before-input">
                <span class="days-prefix">提前</span>
                <input
                  v-model.number="form.daysBefore"
                  type="number"
                  class="form-control"
                  placeholder="3"
                  min="1"
                  max="30"
                  style="width:90px;text-align:center;"
                  @focus="$event.target.select()"
                />
                <span class="days-suffix">天發送</span>
              </div>
              <small class="form-text">可設定 1 ~ 30 天</small>
            </div>
          </div>

          </form>
        </div>
      </div>

      <!-- ======== Tab 2：點數 & 優惠券 ======== -->
      <div v-show="activeTab === 'rewards'" class="card">
        <div class="card-body">
          <form @submit.prevent="handleSubmit">

            <!-- 設定模式選擇 -->
            <div class="form-section">
              <h2 class="section-title">設定模式</h2>
              <div class="form-group">
                <div class="mode-toggle-row">
                  <span class="mode-option" :class="{ active: form.settingMode === 'unified' }">
                    統一設定
                  </span>
                  <label class="toggle-switch">
                    <input
                      type="checkbox"
                      :checked="form.settingMode === 'tier_based'"
                      @change="form.settingMode = $event.target.checked ? 'tier_based' : 'unified'"
                    />
                    <span class="toggle-slider"></span>
                  </label>
                  <span class="mode-option" :class="{ active: form.settingMode === 'tier_based' }">
                    依會員等級設定
                  </span>
                </div>
                <p class="mode-desc">
                  <template v-if="form.settingMode === 'unified'">所有會員使用相同的點數與優惠券設定<span v-if="isWixtarClient">，以及 Wixtar 票券</span></template>
                  <template v-else>不同等級的會員使用各自的點數與優惠券設定<span v-if="isWixtarClient">，以及 Wixtar 票券</span></template>
                </p>
              </div>
            </div>

          <!-- 統一設定（當 settingMode === 'unified' 時顯示） -->
          <template v-if="form.settingMode === 'unified'">
            <!-- 點數設定 -->
            <div class="form-section">
              <h2 class="section-title">點數設定</h2>
              
              <div class="form-group">
                <div class="toggle-row">
                  <label class="toggle-label">發送點數</label>
                  <label class="toggle-switch">
                    <input v-model="form.sendPoints" type="checkbox" />
                    <span class="toggle-slider"></span>
                  </label>
                  <span class="toggle-status" :class="form.sendPoints ? 'on' : 'off'">
                    {{ form.sendPoints ? '已啟用' : '已停用' }}
                  </span>
                </div>
              </div>

              <div v-if="form.sendPoints" class="form-group">
                <label class="form-label">點數數量 <span class="required">*</span></label>
                <input
                  v-model.number="form.pointsAmount"
                  type="number"
                  class="form-control"
                  placeholder="請輸入大於 0 的點數"
                  min="1"
                  @focus="$event.target.select()"
                />
              </div>

              <div v-if="form.sendPoints" class="form-group">
                <p class="form-text" style="margin-bottom: 0;">
                  贈點到期日依「客戶設定 → 點數過期設定」統一計算，與消費贈點相同。
                </p>
              </div>
            </div>

            <!-- 優惠券設定 -->
            <div class="form-section">
              <h2 class="section-title">優惠券設定</h2>
              
              <div class="form-group">
                <div class="toggle-row">
                  <label class="toggle-label">發送優惠券</label>
                  <label class="toggle-switch">
                    <input v-model="form.sendCoupons" type="checkbox" />
                    <span class="toggle-slider"></span>
                  </label>
                  <span class="toggle-status" :class="form.sendCoupons ? 'on' : 'off'">
                    {{ form.sendCoupons ? '已啟用' : '已停用' }}
                  </span>
                </div>
              </div>

              <div v-if="form.sendCoupons" class="form-group">
                <label class="form-label">優惠券模板 <span class="required">*</span></label>
              <div v-if="templatesLoading" class="loading-templates">
                <i class="ri-loader-4-line spin"></i>
                <span>載入優惠券模板中...</span>
              </div>
              <div v-else class="template-selector">
                <div class="template-search">
                  <input
                    v-model="templateSearchQuery"
                    type="text"
                    class="form-control"
                    placeholder="搜尋優惠券模板..."
                    @input="handleTemplateSearch"
                    @focus="$event.target.select()"
                  />
                </div>
                <div class="template-list">
                  <div
                    v-for="template in filteredTemplates"
                    :key="template.id"
                    class="template-item"
                    :class="{ 'selected': isTemplateSelected(template.id) }"
                    @click="toggleTemplate(template.id)"
                  >
                    <input
                      type="checkbox"
                      :checked="isTemplateSelected(template.id)"
                      @change="toggleTemplate(template.id)"
                      @click.stop
                    />
                    <div class="template-info">
                      <div class="template-name">{{ template.templateName }}</div>
                      <RewardStockWarning :template="template" />
                      <div class="template-details">
                        <span class="template-type">{{ getCouponTypeText(template.couponType) }}</span>
                        <span v-if="template.discountValue" class="template-discount">
                          {{ formatDiscount(template.discountValue, template.discountUnit) }}
                        </span>
                        <span v-if="template.validityDays" class="template-validity">
                          有效 {{ template.validityDays }} 天
                        </span>
                      </div>
                    </div>
                  </div>
                  <div v-if="filteredTemplates.length === 0" class="no-templates">
                    <i class="ri-inbox-line"></i>
                    <p>沒有找到優惠券模板</p>
                    <small v-if="templateSearchQuery">請嘗試其他搜尋關鍵字</small>
                  </div>
                </div>
              </div>
              <div v-if="selectedTemplates.length > 0" class="selected-templates">
                <div class="selected-label">已選取 {{ selectedTemplates.length }} 個優惠券模板：</div>
                <div class="selected-tags">
                  <span
                    v-for="template in selectedTemplates"
                    :key="template.id"
                    class="selected-tag"
                  >
                    {{ template.templateName }}
                    <button
                      type="button"
                      class="remove-tag"
                      @click="removeTemplate(template.id)"
                    >
                      <i class="ri-close-line"></i>
                    </button>
                  </span>
                </div>
                <RewardStockWarning
                  v-for="template in selectedTemplates"
                  :key="`stock-${template.id}`"
                  :template="template"
                />
              </div>
                <small class="form-text">可選擇多個優惠券模板，週年禮會發送所有選中的優惠券</small>
              </div>
            </div>

            <div v-if="isWixtarClient" class="form-section">
              <h2 class="section-title">Wixtar 票券設定</h2>

              <div class="form-group">
                <div class="toggle-row">
                  <label class="toggle-label">發送 Wixtar 票券</label>
                  <label class="toggle-switch">
                    <input v-model="form.sendWixtarTickets" type="checkbox" />
                    <span class="toggle-slider"></span>
                  </label>
                  <span class="toggle-status" :class="form.sendWixtarTickets ? 'on' : 'off'">
                    {{ form.sendWixtarTickets ? '已啟用' : '已停用' }}
                  </span>
                </div>
              </div>

              <div v-if="form.sendWixtarTickets" class="form-group">
                <label class="form-label">Wixtar 票券 <span class="required">*</span></label>
                <WixtarTicketMultiSelect
                  :model-value="form.wixtarTicketIds"
                  :tickets="wixtarTickets"
                  :loading="wixtarTicketsLoading"
                  @update:model-value="setUnifiedWixtarTicketIds"
                />
                <small class="form-text">可選擇多張 Wixtar 票券，週年禮會各發送一張。會員需已綁定 Wixtar。</small>
              </div>
            </div>
          </template>

          <!-- 依會員等級設定（當 settingMode === 'tier_based' 時顯示） -->
          <template v-if="form.settingMode === 'tier_based'">
            <div class="form-section">
              <h2 class="section-title">依會員等級設定</h2>
              <p class="section-description">為不同的會員等級設定不同的週年禮內容</p>

              <!-- 未覆蓋等級警示 -->
              <div v-if="unconfiguredTiers.length > 0" class="tier-coverage-warning">
                <i class="ri-error-warning-line"></i>
                <div>
                  <strong>尚有等級未設定：</strong>
                  <span
                    v-for="(t, i) in unconfiguredTiers"
                    :key="t.tierCode"
                    class="missing-tier-tag"
                  >{{ t.tierName }}</span>
                  <span class="warning-tip">儲存前請為所有等級新增設定</span>
                </div>
              </div>
              <div v-else-if="form.tierConfigs.length > 0" class="tier-coverage-ok">
                <i class="ri-checkbox-circle-line"></i> 所有會員等級皆已設定
              </div>
              
              <div v-for="(tierConfig, index) in form.tierConfigs" :key="`tier-${index}-${tierConfig.memberTier || index}`" class="tier-config-card">
                <div class="tier-config-header">
                  <div class="tier-config-title">
                    <i class="ri-medal-line"></i>
                    <span>{{ getTierName(tierConfig.memberTier) }}</span>
                  </div>
                </div>

                <div class="tier-config-body">

                  <!-- 點數設定 -->
                  <div class="form-group">
                    <div class="toggle-row">
                      <label class="toggle-label">發送點數</label>
                      <label class="toggle-switch">
                        <input v-model="tierConfig.sendPoints" type="checkbox" />
                        <span class="toggle-slider"></span>
                      </label>
                      <span class="toggle-status" :class="tierConfig.sendPoints ? 'on' : 'off'">
                        {{ tierConfig.sendPoints ? '已啟用' : '已停用' }}
                      </span>
                    </div>
                  </div>

                  <div v-if="tierConfig.sendPoints" class="form-group">
                    <label class="form-label">點數數量 <span class="required">*</span></label>
                    <input
                      v-model.number="tierConfig.pointsAmount"
                      type="number"
                      class="form-control"
                      :class="{ 'is-invalid': errors[`tierConfigs.${index}.pointsAmount`] }"
                      placeholder="請輸入大於 0 的點數"
                      min="1"
                      @focus="$event.target.select()"
                    />
                    <div v-if="errors[`tierConfigs.${index}.pointsAmount`]" class="invalid-feedback">
                      {{ errors[`tierConfigs.${index}.pointsAmount`] }}
                    </div>
                  </div>

                  <div v-if="tierConfig.sendPoints" class="form-group">
                    <p class="form-text" style="margin-bottom: 0;">
                      贈點到期日依「客戶設定 → 點數過期設定」統一計算，與消費贈點相同。
                    </p>
                  </div>

                  <!-- 優惠券設定 -->
                  <div class="form-group">
                    <div class="toggle-row">
                      <label class="toggle-label">發送優惠券</label>
                      <label class="toggle-switch">
                        <input v-model="tierConfig.sendCoupons" type="checkbox" />
                        <span class="toggle-slider"></span>
                      </label>
                      <span class="toggle-status" :class="tierConfig.sendCoupons ? 'on' : 'off'">
                        {{ tierConfig.sendCoupons ? '已啟用' : '已停用' }}
                      </span>
                    </div>
                  </div>

                  <div v-if="tierConfig.sendCoupons" class="form-group">
                    <label class="form-label">優惠券模板 <span class="required">*</span></label>
                    <div v-if="templatesLoading" class="loading-templates">
                      <i class="ri-loader-4-line spin"></i>
                      <span>載入優惠券模板中...</span>
                    </div>
                    <div v-else class="template-selector">
                      <div class="template-search">
                        <input
                          :value="getTierSearchQuery(index)"
                          @input="setTierSearchQuery(index, $event.target.value)"
                          type="text"
                          class="form-control"
                          placeholder="搜尋優惠券模板..."
                          @focus="$event.target.select()"
                        />
                      </div>
                      <div class="template-list">
                        <div
                          v-for="template in getFilteredTemplatesForTier(index)"
                          :key="template.id"
                          class="template-item"
                          :class="{ 'selected': isTierTemplateSelected(template.id, index) }"
                          @click="toggleTierTemplate(template.id, index)"
                        >
                          <input
                            type="checkbox"
                            :checked="isTierTemplateSelected(template.id, index)"
                            @change="toggleTierTemplate(template.id, index)"
                            @click.stop
                          />
                          <div class="template-info">
                            <div class="template-name">{{ template.templateName }}</div>
                            <RewardStockWarning :template="template" />
                            <div class="template-details">
                              <span class="template-type">{{ getCouponTypeText(template.couponType) }}</span>
                              <span v-if="template.discountValue" class="template-discount">
                                {{ formatDiscount(template.discountValue, template.discountUnit) }}
                              </span>
                              <span v-if="template.validityDays" class="template-validity">
                                有效 {{ template.validityDays }} 天
                              </span>
                            </div>
                          </div>
                        </div>
                        <div v-if="getFilteredTemplatesForTier(index).length === 0" class="no-templates">
                          <i class="ri-inbox-line"></i>
                          <p>沒有找到優惠券模板</p>
                          <small v-if="tierTemplateSearchQueries[index]">請嘗試其他搜尋關鍵字</small>
                        </div>
                      </div>
                    </div>
                    <div v-if="getSelectedTierTemplates(index).length > 0" class="selected-templates">
                      <div class="selected-label">已選取 {{ getSelectedTierTemplates(index).length }} 個優惠券模板：</div>
                      <div class="selected-tags">
                        <span
                          v-for="template in getSelectedTierTemplates(index)"
                          :key="template.id"
                          class="selected-tag"
                        >
                          {{ template.templateName }}
                          <button
                            type="button"
                            class="remove-tag"
                            @click="removeTierTemplate(template.id, index)"
                          >
                            <i class="ri-close-line"></i>
                          </button>
                        </span>
                      </div>
                      <RewardStockWarning
                        v-for="template in getSelectedTierTemplates(index)"
                        :key="`stock-${index}-${template.id}`"
                        :template="template"
                      />
                    </div>
                    <div v-if="errors[`tierConfigs.${index}.couponTemplateIds`]" class="invalid-feedback" style="display:block;">
                      {{ errors[`tierConfigs.${index}.couponTemplateIds`] }}
                    </div>
                    <small class="form-text">可選擇多個優惠券模板，週年禮會發送所有選中的優惠券</small>
                  </div>

                  <div v-if="isWixtarClient" class="form-group">
                    <div class="toggle-row">
                      <label class="toggle-label">發送 Wixtar 票券</label>
                      <label class="toggle-switch">
                        <input v-model="tierConfig.sendWixtarTickets" type="checkbox" />
                        <span class="toggle-slider"></span>
                      </label>
                      <span class="toggle-status" :class="tierConfig.sendWixtarTickets ? 'on' : 'off'">
                        {{ tierConfig.sendWixtarTickets ? '已啟用' : '已停用' }}
                      </span>
                    </div>
                  </div>

                  <div v-if="isWixtarClient && tierConfig.sendWixtarTickets" class="form-group">
                    <label class="form-label">Wixtar 票券 <span class="required">*</span></label>
                    <WixtarTicketMultiSelect
                      :model-value="tierConfig.wixtarTicketIds || []"
                      :tickets="wixtarTickets"
                      :loading="wixtarTicketsLoading"
                      @update:model-value="setTierWixtarTicketIds(index, $event)"
                    />
                    <div v-if="errors[`tierConfigs.${index}.wixtarTicketIds`]" class="invalid-feedback" style="display:block;">
                      {{ errors[`tierConfigs.${index}.wixtarTicketIds`] }}
                    </div>
                    <small class="form-text">可選擇多張 Wixtar 票券，週年禮會各發送一張。會員需已綁定 Wixtar。</small>
                  </div>
                </div>
              </div>

              <small class="form-text" style="color:#6c757d;">
                <i class="ri-information-line"></i>
                設定區塊依會員等級自動產生，每個等級可個別設定點數與優惠券內容<span v-if="isWixtarClient">，以及 Wixtar 票券</span>
              </small>
            </div>
          </template>

          </form>
        </div>
      </div>

      <!-- ======== Tab 3：通知設定 ======== -->
      <div v-show="activeTab === 'notification'" class="card">
        <div class="card-body">
          <form @submit.prevent="handleSubmit">

            <!-- LINE 官方帳號發送設定 -->
            <div class="form-section">
              <h2 class="section-title">LINE 官方帳號</h2>
              <p class="section-desc">選擇要透過哪些 LINE 官方帳號發送週年禮。若不選擇，系統將以會員所加入的第一個帳號發送。</p>

              <div v-if="lineConfigsLoading" class="text-muted" style="padding:8px 0;">
                <i class="ri-loader-4-line spin"></i> 載入中...
              </div>
              <div v-else-if="lineConfigs.length === 0" class="text-muted" style="padding:8px 0;">
                尚未設定任何 LINE 官方帳號
              </div>
              <div v-else class="line-config-list">
                <label
                  v-for="lc in lineConfigs"
                  :key="lc.id"
                  class="line-config-item"
                >
                  <input
                    type="checkbox"
                    :value="lc.id"
                    v-model="form.lineConfigIds"
                    class="line-config-checkbox"
                  />
                  <span class="line-config-avatar" v-if="lc.botPictureUrl || lc.bot_picture_url">
                    <img :src="lc.botPictureUrl || lc.bot_picture_url" alt="LINE avatar" />
                  </span>
                  <span class="line-config-avatar placeholder" v-else>
                    <i class="ri-line-fill"></i>
                  </span>
                  <span class="line-config-name">
                    {{ lc.botDisplayName || lc.bot_display_name || lc.configName || lc.config_name || '未命名帳號' }}
                  </span>
                  <span class="line-config-id text-muted" v-if="lc.messagingBotBasicId || lc.messaging_bot_basic_id || lc.botBasicId || lc.bot_basic_id">
                    {{ lc.messagingBotBasicId || lc.messaging_bot_basic_id || lc.botBasicId || lc.bot_basic_id }}
                  </span>
                </label>
              </div>

              <!-- 多帳號警告 -->
              <div v-if="form.lineConfigIds.length > 1" class="alert alert-warning line-multi-warning" style="margin-top:12px;">
                <i class="ri-error-warning-line"></i>
                <div>
                  <strong>已選取 {{ form.lineConfigIds.length }} 個帳號</strong><br>
                  若會員同時加入多個已選帳號為好友，將分別收到多份週年禮（包含點數、優惠券與通知訊息）。請確認此為預期行為。
                </div>
              </div>
            </div>

            <div class="form-section">
              <h2 class="section-title">通知設定</h2>
              <NotificationChannelSelector
                v-model:channels="form.notificationChannels"
                v-model:templateIds="form.notificationTemplateIds"
                show-default-template-preview
              />
              <span v-if="errors.notificationChannels" class="error-text" style="display:block;margin-top:8px;">
                {{ errors.notificationChannels }}
              </span>
            </div>
          </form>
        </div>
      </div>

      <!-- ======== Tab 4：發送紀錄 ======== -->
      <div v-show="activeTab === 'records'" class="card">
        <div class="card-body">

          <!-- 手動補發 -->
          <div class="manual-send-panel">
            <h2 class="section-title">手動補發</h2>
            <p class="section-desc">搜尋會員後補發今年度的週年禮；已成功發放過的年度會被拒絕重複補發。</p>
            <MemberSearch ref="manualSendMemberSearch" v-model="manualSendMembers" :disabled="manualSending" />
            <div class="manual-send-actions">
              <button
                type="button"
                class="btn btn-primary"
                :disabled="manualSending || manualSendMembers.length === 0"
                @click="handleManualSend"
              >
                <span v-if="manualSending"><i class="ri-loader-4-line spin"></i> 補發中...</span>
                <span v-else><i class="ri-gift-line"></i> 補發週年禮（{{ manualSendMembers.length }} 位會員）</span>
              </button>
            </div>
          </div>

          <!-- 篩選工具欄（始終顯示） -->
          <div class="page-toolbar">
            <div class="filter-group">
              <label class="filter-label">狀態</label>
              <select v-model="logsFilter.status" class="form-control">
                <option value="">全部狀態</option>
                <option value="success">成功</option>
                <option value="failed">失敗</option>
                <option value="pending">處理中</option>
                <option value="retrying">重試中</option>
              </select>
            </div>
            <div class="filter-group">
              <label class="filter-label">會員名稱</label>
              <input
                v-model="logsFilter.memberName"
                type="text"
                class="form-control"
                placeholder="搜尋會員名稱..."
                @keyup.enter="searchLogs"
                @focus="$event.target.select()"
              />
            </div>
            <div class="filter-group">
              <label class="filter-label">發送日期（起）</label>
              <input v-model="logsFilter.startDate" type="date" class="form-control" />
            </div>
            <div class="filter-group">
              <label class="filter-label">發送日期（迄）</label>
              <input v-model="logsFilter.endDate" type="date" class="form-control" />
            </div>
            <div class="filter-group filter-group--action">
              <button class="btn btn-primary" @click="searchLogs">
                <i class="ri-search-line"></i> 查詢
              </button>
              <button class="btn btn-outline-secondary" @click="resetLogsFilter">
                <i class="ri-refresh-line"></i> 重置
              </button>
            </div>
          </div>

          <!-- 尚未查詢的空狀態 -->
          <div v-if="!logsSearched" class="logs-empty-hint">
            <i class="ri-search-line"></i>
            <p>請輸入查詢條件後點擊「查詢」</p>
          </div>

          <!-- 查詢結果區域 -->
          <div v-else>
            <!-- 發送統計 -->
            <h2 class="section-title" style="margin-top: 16px;">發送統計</h2>
            <div v-if="statisticsLoading" class="loading-state">
              <div class="spinner"></div>
              <p>載入統計資料中...</p>
            </div>
            <div v-else>
              <div class="stats-cards">
                <div class="stat-card">
                  <div class="stat-label">總發送數</div>
                  <div class="stat-value">{{ statistics.totalSent || 0 }}</div>
                </div>
                <div class="stat-card">
                  <div class="stat-label">成功發送數</div>
                  <div class="stat-value">{{ statistics.successSent || 0 }}</div>
                </div>
                <div class="stat-card">
                  <div class="stat-label">失敗發送數</div>
                  <div class="stat-value">{{ statistics.failedSent || 0 }}</div>
                </div>
                <div class="stat-card">
                  <div class="stat-label">成功率</div>
                  <div class="stat-value">{{ statistics.successRate || 0 }}%</div>
                </div>
              </div>

              <!-- 趨勢圖表 -->
              <div v-if="trendData.length > 0" class="chart-section" style="margin-top: 24px;">
                <div class="chart-card">
                  <TrendChart
                    title="發送趨勢（近30天）"
                    :data="trendData"
                    xAxisKey="date"
                    yAxisKey="count"
                    color="#4facfe"
                    height="300px"
                  />
                </div>
              </div>
            </div>

            <!-- 發送記錄表格 -->
            <div class="table-section" style="margin-top: 24px;">
              <h3 class="section-subtitle">發送記錄</h3>
              <div v-if="logsLoading" class="loading-state">
                <div class="spinner"></div>
                <p>載入中...</p>
              </div>
              <div v-else>
                <div class="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>發送時間</th>
                        <th>會員名稱</th>
                        <th>第幾週年</th>
                        <th>點數</th>
                        <th>優惠券</th>
                        <th v-if="isWixtarClient">Wixtar 票券</th>
                        <th>通知管道</th>
                        <th>狀態</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="logs.length === 0">
                        <td :colspan="logsColspan" class="text-center text-muted">尚無發送記錄</td>
                      </tr>
                      <template v-for="log in logs" :key="log.id">
                        <tr
                          class="log-row"
                          :class="{ expanded: expandedLogId === log.id }"
                          @click="toggleLogExpand(log.id)"
                        >
                          <td>{{ formatDateTime(log.sentAt || log.createdAt) }}</td>
                          <td>{{ log.memberName || '-' }}</td>
                          <td>{{ log.anniversaryYears != null ? `${log.anniversaryYears} 週年` : '-' }}</td>
                          <td>{{ log.pointsSent || 0 }} 點</td>
                          <td>{{ log.couponsSent || 0 }} 張</td>
                          <td v-if="isWixtarClient">{{ log.wixtarTicketsSent || 0 }} 張</td>
                          <td>
                            <div class="notification-icons">
                              <template v-if="log.notificationResults">
                                <span
                                  v-for="ch in ['line', 'email', 'sms']"
                                  :key="ch"
                                  v-show="log.notificationResults[ch]"
                                  :class="['notif-icon', ch, log.notificationResults[ch] && log.notificationResults[ch].sent ? 'sent' : 'failed']"
                                  :title="getNotifTitle(log.notificationResults[ch], ch)"
                                >
                                  <i :class="channelIcon(ch)"></i>
                                </span>
                              </template>
                              <template v-else>
                                <span v-if="log.notificationSent" class="badge badge-success">已發送</span>
                                <span v-else class="badge badge-secondary">未發送</span>
                              </template>
                            </div>
                          </td>
                          <td>
                            <span :class="getStatusBadgeClass(log.status)">
                              {{ getStatusText(log.status) }}
                            </span>
                          </td>
                          <td>
                            <i :class="expandedLogId === log.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                          </td>
                        </tr>
                        <!-- 展開詳情列 -->
                        <tr v-if="expandedLogId === log.id" class="log-detail-row">
                          <td :colspan="logsColspan">
                            <div class="log-detail">
                              <div class="log-detail-grid">
                                <div>
                                  <strong>週年資訊：</strong>
                                  <div v-if="log.anniversaryYears != null">第 {{ log.anniversaryYears }} 週年</div>
                                  <div v-if="log.registrationDate">註冊日：{{ formatDate(log.registrationDate) }}</div>
                                </div>
                                <div>
                                  <strong>禮物明細：</strong>
                                  <div v-if="log.pointsSent > 0">點數：{{ log.pointsSent }} 點</div>
                                  <div v-if="log.couponsSent > 0">優惠券：{{ log.couponsSent }} 張</div>
                                  <div v-if="isWixtarClient && (log.wixtarTicketsSent > 0 || (log.wixtarTicketDetails && log.wixtarTicketDetails.length))">
                                    Wixtar 票券：{{ log.wixtarTicketsSent || 0 }} 張
                                  </div>
                                  <div v-if="!log.pointsSent && !log.couponsSent && !(isWixtarClient && log.wixtarTicketsSent)" class="text-muted">無禮物</div>
                                </div>
                                <div v-if="log.notificationResults">
                                  <strong>通知結果：</strong>
                                  <div
                                    v-for="ch in ['line', 'email', 'sms']"
                                    :key="ch"
                                    v-show="log.notificationResults[ch]"
                                    class="notif-result-row"
                                  >
                                    <i :class="channelIcon(ch)"></i>
                                    <span class="ch-label">{{ channelLabel(ch) }}</span>
                                    <template v-if="log.notificationResults[ch]">
                                      <span v-if="log.notificationResults[ch].sent" class="result-ok">
                                        <i class="ri-checkbox-circle-line"></i> 已發送
                                        <small v-if="log.notificationResults[ch].sentAt">{{ formatDateTime(log.notificationResults[ch].sentAt) }}</small>
                                      </span>
                                      <span v-else class="result-fail">
                                        <i class="ri-close-circle-line"></i> 失敗
                                        <small v-if="log.notificationResults[ch].error">{{ log.notificationResults[ch].error }}</small>
                                      </span>
                                    </template>
                                  </div>
                                </div>
                                <div v-if="log.errorMessage">
                                  <strong>錯誤訊息：</strong>
                                  <div class="error-msg">{{ log.errorMessage }}</div>
                                  <div v-if="log.errorCode" class="error-code">錯誤代碼：{{ log.errorCode }}</div>
                                </div>
                                <div v-if="log.retryCount > 0">
                                  <strong>重試次數：</strong> {{ log.retryCount }} 次
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </div>
                <ListPagination
                  v-if="logsPagination.totalPages > 1"
                  :page="logsPagination.page"
                  :total-pages="logsPagination.totalPages"
                  :total="logsPagination.total"
                  @change="changeLogsPage"
                />
              </div>
            </div>
          </div><!-- end v-else (logsSearched) -->

        </div>
      </div>
    </template>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { autoSendService } from '@/api/modules/autoSend.js';
import { couponService } from '@/api/modules/coupons.js';
import { memberTierService } from '@/api/modules/memberTiers.js';
import { wixtarService } from '@/api/modules/wixtar.js';
import { clientService } from '@/api/modules/clients.js';
import lineConfigService from '@/api/modules/lineConfig.js';
import authService from '@/helpers/authservice/authService.js';
import { showSuccess, showError } from '@/utils/swal.js';
import TrendChart from '@/components/charts/TrendChart.vue';
import NotificationChannelSelector from '@/components/NotificationChannelSelector.vue';
import MemberSearch from '@/components/MemberSearch.vue';
import WixtarTicketMultiSelect from '@/components/WixtarTicketMultiSelect.vue';
import RewardStockWarning from '@/components/RewardStockWarning.vue';

export default {
  name: 'AnniversaryGiftConfig',
  components: { TrendChart, NotificationChannelSelector, MemberSearch, WixtarTicketMultiSelect, RewardStockWarning },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { pageTitle } = usePageTitle('週年禮設定');
    const loading = ref(false);
    const submitting = ref(false);
    const activeTab = ref('basic');
    const currentModuleId = ref(route.params.moduleId || null);
    
    const form = reactive({
      moduleName: '預設模組',
      activeStartAtLocal: '',
      activeEndAtLocal: '',
      isEnabled: true,
      dailySendTime: '09:00',
      sendTiming: 'anniversary_day',
      daysBefore: null,
      settingMode: 'unified', // 'unified' 或 'tier_based'
      sendPoints: false,
      pointsAmount: 0,
      sendCoupons: false,
      couponTemplateIds: [],
      sendWixtarTickets: false,
      wixtarTicketIds: [],
      tierConfigs: [], // 依等級設定
      sendNotification: false,
      notificationChannel: null,
      customMessageTemplate: null,
      notificationChannels: [],
      notificationTemplateIds: {},
      lineConfigIds: []
    });

    const lineConfigs = ref([]);
    const lineConfigsLoading = ref(false);

    const errors = reactive({});
    const templates = ref([]);
    const templatesLoading = ref(false);
    const isWixtarClient = ref(false);
    const memberSystemLoaded = ref(false);
    const wixtarTickets = ref([]);
    const wixtarTicketsLoading = ref(false);
    const logsColspan = computed(() => (isWixtarClient.value ? 9 : 8));
    const templateSearchQuery = ref('');
    const tiers = ref([]);
    const tiersLoading = ref(false);
    const tierTemplateSearchQueries = ref({}); // 使用 tierConfig 的唯一 key 作為索引

    
    // 發送統計相關
    const statistics = ref({
      totalSent: 0,
      successSent: 0,
      failedSent: 0,
      successRate: 0
    });
    const statisticsLoading = ref(false);
    const logs = ref([]);
    const logsLoading = ref(false);
    const logsSearched = ref(false);
    const logsFilter = reactive({
      status: '',
      memberName: '',
      startDate: '',
      endDate: ''
    });
    const expandedLogId = ref(null);

    // 手動補發
    const manualSendMembers = ref([]);
    const manualSending = ref(false);
    const manualSendMemberSearch = ref(null);

    const handleManualSend = async () => {
      if (manualSendMembers.value.length === 0) return;

      const names = manualSendMembers.value.map(m => m.name || m.id).join('、');
      const confirmed = window.confirm(`確定要為以下會員補發今年度的週年禮嗎？\n${names}`);
      if (!confirmed) return;

      manualSending.value = true;
      const failures = [];
      let successCount = 0;
      try {
        for (const member of manualSendMembers.value) {
          try {
            const response = await autoSendService.manualSendAnniversaryGift({ memberId: member.id });
            if (response?.success) {
              successCount++;
            } else {
              failures.push(`${member.name || member.id}：${response?.message || '未知錯誤'}`);
            }
          } catch (error) {
            failures.push(`${member.name || member.id}：${error.message || '補發失敗'}`);
          }
        }

        if (failures.length === 0) {
          await showSuccess('補發已送出', `已為 ${successCount} 位會員送出週年禮補發（實際發送結果請於發送紀錄查詢）`);
          manualSendMembers.value = [];
          manualSendMemberSearch.value?.reset?.();
        } else {
          showError(
            successCount > 0 ? `部分補發失敗（成功 ${successCount} 位）` : '補發失敗',
            failures.join('\n')
          );
        }

        if (logsSearched.value) {
          await loadStatistics();
          await loadLogs();
        }
      } finally {
        manualSending.value = false;
      }
    };

    const toggleLogExpand = (id) => {
      expandedLogId.value = expandedLogId.value === id ? null : id;
    };

    const channelIcon = (ch) => ({
      line: 'ri-line-fill',
      email: 'ri-mail-line',
      sms: 'ri-message-2-line'
    })[ch] || 'ri-notification-line';

    const channelLabel = (ch) => ({ line: 'LINE', email: 'Email', sms: 'SMS' })[ch] || ch;

    const getNotifTitle = (result, ch) => {
      if (!result) return channelLabel(ch);
      if (result.sent) return `${channelLabel(ch)}：已發送`;
      return `${channelLabel(ch)}：失敗 - ${result.error || '未知'}`;
    };
    const logsPagination = reactive({
      page: 1,
      pageSize: 20,
      total: 0,
      totalPages: 0
    });
    const trendData = ref([]);
    const logsSearchTimeout = ref(null);

    // 將 TIME 格式（HH:mm:ss）轉換為 time input 格式（HH:mm）
    const formatTimeForInput = (timeString) => {
      if (!timeString) return '09:00';
      // 如果是 HH:mm:ss 格式，只取前 5 個字元（HH:mm）
      if (timeString.length >= 5) {
        return timeString.substring(0, 5);
      }
      return timeString;
    };

    // 將 time input 格式（HH:mm）轉換為 TIME 格式（HH:mm:ss）
    const formatTimeForApi = (timeString) => {
      if (!timeString) return '09:00:00';
      // 如果已經是 HH:mm:ss 格式，直接返回
      if (timeString.length === 8) {
        return timeString;
      }
      // 如果是 HH:mm 格式，加上 :00
      if (timeString.length === 5) {
        return `${timeString}:00`;
      }
      return '09:00:00';
    };

    const toDateInputValue = (isoString) => {
      if (!isoString) return '';
      const date = new Date(isoString);
      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Taipei',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      return formatter.format(date);
    };

    const fromDateInputValue = (dateValue, isEnd = false) => {
      if (!dateValue) return null;
      return isEnd ? `${dateValue}T23:59:59+08:00` : `${dateValue}T00:00:00+08:00`;
    };

    const parseIdList = (value) => {
      if (!value) return [];
      if (Array.isArray(value)) return value.map((id) => String(id));
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed.map((id) => String(id)) : [String(parsed)];
        } catch {
          return [value];
        }
      }
      return [String(value)];
    };

    const applyConfigToForm = (data) => {
      let couponTemplateIdsValue = [];
      if (data.couponTemplateIds) {
        if (Array.isArray(data.couponTemplateIds)) {
          couponTemplateIdsValue = data.couponTemplateIds;
        } else if (typeof data.couponTemplateIds === 'string') {
          try {
            const parsed = JSON.parse(data.couponTemplateIds);
            couponTemplateIdsValue = Array.isArray(parsed) ? parsed : [parsed];
          } catch {
            couponTemplateIdsValue = [];
          }
        } else {
          couponTemplateIdsValue = [data.couponTemplateIds];
        }
      }

      const hasTierConfigs = data.tierConfigs &&
        Array.isArray(data.tierConfigs) &&
        data.tierConfigs.length > 0;

      Object.assign(form, data);
      form.settingMode = hasTierConfigs ? 'tier_based' : 'unified';
      form.couponTemplateIds = couponTemplateIdsValue;
      form.sendWixtarTickets = !!data.sendWixtarTickets;
      form.wixtarTicketIds = parseIdList(data.wixtarTicketIds);
      form.moduleName = data.moduleName || '預設模組';
      form.activeStartAtLocal = toDateInputValue(data.activeStartAt);
      form.activeEndAtLocal = toDateInputValue(data.activeEndAt);

      if (hasTierConfigs) {
        form.tierConfigs = data.tierConfigs.map(config => ({
          memberTier: config.memberTier || '',
          sendPoints: config.sendPoints || false,
          pointsAmount: config.pointsAmount || 0,
          sendCoupons: config.sendCoupons || false,
          couponTemplateIds: Array.isArray(config.couponTemplateIds) ? config.couponTemplateIds : [],
          sendWixtarTickets: !!(config.sendWixtarTickets || parseIdList(config.wixtarTicketIds).length),
          wixtarTicketIds: parseIdList(config.wixtarTicketIds)
        }));

        form.tierConfigs.forEach((config, index) => {
          const key = getTierConfigKey(config, index);
          if (!tierTemplateSearchQueries.value[key]) {
            tierTemplateSearchQueries.value[key] = '';
          }
        });
      } else {
        form.tierConfigs = [];
      }

      if (data.dailySendTime) {
        form.dailySendTime = formatTimeForInput(data.dailySendTime);
      }

      if (form.couponTemplateIds && form.couponTemplateIds.length > 0) {
        form.sendCoupons = true;
      }
      if (form.wixtarTicketIds.length > 0) {
        form.sendWixtarTickets = true;
      }

      if (form.settingMode === 'tier_based') {
        autoGenerateTierConfigs();
      }

      form.notificationChannels = Array.isArray(data.notificationChannels)
        ? data.notificationChannels : [];
      form.notificationTemplateIds = data.notificationTemplateIds || {};
      form.lineConfigIds = Array.isArray(data.lineConfigIds)
        ? data.lineConfigIds : [];
    };

    // 過濾優惠券模板（根據搜尋關鍵字）
    const filteredTemplates = computed(() => {
      if (!templateSearchQuery.value || !templateSearchQuery.value.trim()) {
        return templates.value;
      }
      const searchLower = templateSearchQuery.value.toLowerCase().trim();
      return templates.value.filter(template =>
        template.templateName.toLowerCase().includes(searchLower) ||
        (template.couponType && template.couponType.toLowerCase().includes(searchLower))
      );
    });

    // 已選取的優惠券模板
    const selectedTemplates = computed(() => {
      if (!form.couponTemplateIds || form.couponTemplateIds.length === 0) {
        return [];
      }
      return templates.value.filter(t => form.couponTemplateIds.includes(t.id));
    });

    // 取得特定 index 的下拉可用等級（排除其他行已選的等級，但保留自己當前已選的）
    const getAvailableTiersForIndex = (index) => {
      const otherSelected = form.tierConfigs
        .map((config, i) => i !== index ? config.memberTier : null)
        .filter(Boolean);
      return tiers.value.filter(tier => !otherSelected.includes(tier.tierCode));
    };

    // 可用的會員等級（用於「新增等級設定」按鈕的 disabled 判斷）
    const availableTiers = computed(() => {
      const selectedTiers = form.tierConfigs.map(config => config.memberTier).filter(Boolean);
      return tiers.value.filter(tier => !selectedTiers.includes(tier.tierCode));
    });

    // 尚未設定的等級（tier_based 模式下的提示用）
    const unconfiguredTiers = computed(() => {
      if (form.settingMode !== 'tier_based') return [];
      const configuredCodes = form.tierConfigs.map(c => c.memberTier).filter(Boolean);
      return tiers.value.filter(t => !configuredCodes.includes(t.tierCode));
    });

    // 取得等級名稱
    const getTierName = (tierCode) => {
      if (!tierCode) return '未選擇';
      const tier = tiers.value.find(t => t.tierCode === tierCode);
      return tier ? tier.tierName : tierCode;
    };

    // 取得等級設定的搜尋查詢（使用穩定的 key）
    const getTierSearchQuery = (index) => {
      const tierConfig = form.tierConfigs[index];
      if (!tierConfig) return '';
      const key = getTierConfigKey(tierConfig, index);
      return tierTemplateSearchQueries.value[key] || '';
    };

    // 設定等級設定的搜尋查詢
    const setTierSearchQuery = (index, value) => {
      const tierConfig = form.tierConfigs[index];
      if (!tierConfig) return;
      const key = getTierConfigKey(tierConfig, index);
      tierTemplateSearchQueries.value[key] = value;
    };

    // 取得 tierConfig 的唯一 key（優先使用 memberTier，否則使用 index）
    const getTierConfigKey = (tierConfig, index) => {
      return tierConfig.memberTier || `index-${index}`;
    };

    // 處理會員等級變更（遷移搜尋查詢）
    const handleTierChange = (index, newTierCode) => {
      const tierConfig = form.tierConfigs[index];
      if (!tierConfig) return;
      
      const oldKey = getTierConfigKey(tierConfig, index);
      const oldQuery = tierTemplateSearchQueries.value[oldKey];
      
      // 如果舊的 key 是 index-based，且有查詢值，遷移到新的 memberTier-based key
      if (oldKey.startsWith('index-') && oldQuery && newTierCode) {
        tierTemplateSearchQueries.value[newTierCode] = oldQuery;
        delete tierTemplateSearchQueries.value[oldKey];
      } else if (newTierCode && !tierTemplateSearchQueries.value[newTierCode]) {
        // 如果新選擇的 tier 還沒有查詢，初始化為空字串
        tierTemplateSearchQueries.value[newTierCode] = '';
      }
    };

    // 依等級設定的優惠券模板相關方法
    const getFilteredTemplatesForTier = (index) => {
      const query = getTierSearchQuery(index);
      if (!query || !query.trim()) {
        return templates.value;
      }
      const searchLower = query.toLowerCase().trim();
      return templates.value.filter(template =>
        template.templateName.toLowerCase().includes(searchLower) ||
        (template.couponType && template.couponType.toLowerCase().includes(searchLower))
      );
    };

    const getSelectedTierTemplates = (index) => {
      const tierConfig = form.tierConfigs[index];
      if (!tierConfig || !tierConfig.couponTemplateIds || tierConfig.couponTemplateIds.length === 0) {
        return [];
      }
      return templates.value.filter(t => tierConfig.couponTemplateIds.includes(t.id));
    };

    const isTierTemplateSelected = (templateId, index) => {
      const tierConfig = form.tierConfigs[index];
      return tierConfig && tierConfig.couponTemplateIds && tierConfig.couponTemplateIds.includes(templateId);
    };

    const toggleTierTemplate = (templateId, index) => {
      const tierConfig = form.tierConfigs[index];
      if (!tierConfig.couponTemplateIds) {
        tierConfig.couponTemplateIds = [];
      }
      const templateIndex = tierConfig.couponTemplateIds.indexOf(templateId);
      if (templateIndex > -1) {
        tierConfig.couponTemplateIds.splice(templateIndex, 1);
      } else {
        tierConfig.couponTemplateIds.push(templateId);
      }
      if (tierConfig.couponTemplateIds.length > 0) {
        tierConfig.sendCoupons = true;
      }
    };

    const removeTierTemplate = (templateId, index) => {
      const tierConfig = form.tierConfigs[index];
      if (tierConfig && tierConfig.couponTemplateIds) {
        const templateIndex = tierConfig.couponTemplateIds.indexOf(templateId);
        if (templateIndex > -1) {
          tierConfig.couponTemplateIds.splice(templateIndex, 1);
        }
        if (tierConfig.couponTemplateIds.length === 0) {
          tierConfig.sendCoupons = false;
        }
      }
    };

    // 依所有等級自動生成 tierConfigs（保留已存在的設定）
    const autoGenerateTierConfigs = () => {
      const existingMap = {};
      form.tierConfigs.forEach(config => {
        if (config.memberTier) existingMap[config.memberTier] = config;
      });

      form.tierConfigs = tiers.value.map((tier, index) => {
        const existing = existingMap[tier.tierCode];
        const config = existing || {
          memberTier: tier.tierCode,
          sendPoints: false,
          pointsAmount: 0,
          sendCoupons: false,
          couponTemplateIds: [],
          sendWixtarTickets: false,
          wixtarTicketIds: []
        };
        if (!existing) config.memberTier = tier.tierCode;
        if (!config.wixtarTicketIds) config.wixtarTicketIds = [];
        const key = config.memberTier || `index-${index}`;
        if (!tierTemplateSearchQueries.value[key]) {
          tierTemplateSearchQueries.value[key] = '';
        }
        return config;
      });
    };

    // 監聽 settingMode，切換到 tier_based 時自動生成
    watch(() => form.settingMode, (newMode) => {
      if (newMode === 'tier_based') {
        autoGenerateTierConfigs();
      }
    });

    // 新增/移除等級設定
    const addTierConfig = () => {
      if (availableTiers.value.length === 0) {
        showError('無法新增', '沒有可用的會員等級');
        return;
      }
      const newConfig = {
        memberTier: '',
        sendPoints: false,
        pointsAmount: 0,
        sendCoupons: false,
        couponTemplateIds: [],
        sendWixtarTickets: false,
        wixtarTicketIds: []
      };
      form.tierConfigs.push(newConfig);
      const index = form.tierConfigs.length - 1;
      const key = getTierConfigKey(newConfig, index);
      tierTemplateSearchQueries.value[key] = '';
    };

    const removeTierConfig = (index) => {
      const tierConfig = form.tierConfigs[index];
      if (tierConfig) {
        // 如果已經選擇了 memberTier，使用它作為 key 來刪除
        if (tierConfig.memberTier) {
          delete tierTemplateSearchQueries.value[tierConfig.memberTier];
        }
        // 也刪除可能存在的 index-based key
        delete tierTemplateSearchQueries.value[`index-${index}`];
      }
      form.tierConfigs.splice(index, 1);
      
      // 重新建立搜尋查詢的 key（因為索引改變了）
      // 保留所有使用 memberTier 作為 key 的查詢（這些不會改變）
      // 只重新映射使用 index 作為 key 的查詢
      const newQueries = {};
      Object.keys(tierTemplateSearchQueries.value).forEach(key => {
        // 如果 key 是 memberTier（不是 index-開頭），直接保留
        if (!key.startsWith('index-')) {
          newQueries[key] = tierTemplateSearchQueries.value[key];
        }
      });
      
      // 重新映射剩餘的 tierConfigs 的 index-based keys
      form.tierConfigs.forEach((config, newIndex) => {
        // 如果這個 config 還沒有 memberTier，使用新的 index
        if (!config.memberTier) {
          const oldIndex = index; // 被刪除的索引
          // 檢查是否有舊的 index-based key 需要遷移
          // 但實際上，由於我們已經刪除了被刪除的 config 的查詢，這裡只需要確保新的 config 有對應的查詢
          if (!newQueries[`index-${newIndex}`]) {
            newQueries[`index-${newIndex}`] = '';
          }
        }
      });
      
      tierTemplateSearchQueries.value = newQueries;
    };

    // 載入會員等級列表
    const loadTiers = async () => {
      tiersLoading.value = true;
      try {
        const response = await memberTierService.getTiers({
          isActive: true,
          sortBy: 'displayOrder',
          order: 'asc'
        });
        if (response && response.success && response.data) {
          tiers.value = Array.isArray(response.data) ? response.data : (response.data.items || []);
        }
      } catch (error) {
        console.error('載入會員等級失敗:', error);
        showError('載入會員等級失敗', error.message);
      } finally {
        tiersLoading.value = false;
      }
    };

    // 載入優惠券模板列表
    const loadTemplates = async () => {
      templatesLoading.value = true;
      try {
        const response = await couponService.getTemplates({
          pageSize: 100,
          isActive: true
        });
        if (response && response.success && response.data) {
          templates.value = response.data.items || [];
          console.log('[AnniversaryGiftConfig] 載入模板完成，共', templates.value.length, '個模板');
          console.log('[AnniversaryGiftConfig] 模板 ID 列表:', templates.value.map(t => t.id));
          
          // 載入模板後，檢查已選的模板是否在列表中
          if (form.couponTemplateIds && form.couponTemplateIds.length > 0) {
            console.log('[AnniversaryGiftConfig] 已選的模板 ID:', form.couponTemplateIds);
            const foundTemplates = templates.value.filter(t => form.couponTemplateIds.includes(t.id));
            console.log('[AnniversaryGiftConfig] 找到的模板:', foundTemplates.map(t => ({ id: t.id, name: t.templateName })));
            const notFoundIds = form.couponTemplateIds.filter(id => !templates.value.find(t => t.id === id));
            if (notFoundIds.length > 0) {
              console.warn('[AnniversaryGiftConfig] 未找到的模板 ID:', notFoundIds);
            }
          } else {
            console.log('[AnniversaryGiftConfig] 沒有已選的模板');
          }
        }
      } catch (error) {
        console.error('載入優惠券模板失敗:', error);
        showError('載入優惠券模板失敗', error.message);
      } finally {
        templatesLoading.value = false;
      }
    };

    // 切換通知管道勾選
    const toggleNotificationChannel = (channel, checked) => {
      if (!form.notificationChannels) form.notificationChannels = [];
      if (checked) {
        if (!form.notificationChannels.includes(channel)) {
          form.notificationChannels.push(channel);
        }
      } else {
        const idx = form.notificationChannels.indexOf(channel);
        if (idx >= 0) form.notificationChannels.splice(idx, 1);
        // 清除該管道的範本
        if (form.notificationTemplateIds) {
          form.notificationTemplateIds = { ...form.notificationTemplateIds };
          delete form.notificationTemplateIds[channel];
        }
      }
    };

    // 設定指定管道的範本
    const setNotificationTemplate = (channel, templateId) => {
      if (!form.notificationTemplateIds) form.notificationTemplateIds = {};
      if (templateId) {
        form.notificationTemplateIds = { ...form.notificationTemplateIds, [channel]: templateId };
      } else {
        form.notificationTemplateIds = { ...form.notificationTemplateIds };
        delete form.notificationTemplateIds[channel];
      }
    };

    // 檢查模板是否已選取
    const isTemplateSelected = (templateId) => {
      return form.couponTemplateIds && form.couponTemplateIds.includes(templateId);
    };

    // 切換模板選取狀態
    const toggleTemplate = (templateId) => {
      if (!form.couponTemplateIds) {
        form.couponTemplateIds = [];
      }
      const index = form.couponTemplateIds.indexOf(templateId);
      if (index > -1) {
        form.couponTemplateIds.splice(index, 1);
      } else {
        form.couponTemplateIds.push(templateId);
      }
    };

    // 移除模板
    const removeTemplate = (templateId) => {
      if (form.couponTemplateIds) {
        const index = form.couponTemplateIds.indexOf(templateId);
        if (index > -1) {
          form.couponTemplateIds.splice(index, 1);
        }
      }
    };

    // 處理模板搜尋
    const handleTemplateSearch = () => {
      // 搜尋由 computed 屬性自動處理
    };

    // 取得優惠券類型文字
    const getCouponTypeText = (type) => {
      const typeMap = {
        'discount': '折扣券',
        'fixed_amount': '折價券',
        'free_shipping': '免運券',
        'gift': '贈品券'
      };
      return typeMap[type] || type || '優惠券';
    };

    // 格式化折扣資訊
    const formatDiscount = (value, unit) => {
      if (!value) return '';
      if (unit === 'percent') {
        return `${value}% 折扣`;
      } else if (unit === 'fixed') {
        return `折抵 NT$ ${value}`;
      }
      return `${value} ${unit}`;
    };

    const loadConfig = async (moduleId = currentModuleId.value) => {
      if (!moduleId) {
        showError('缺少模組 ID', '請從模組清單進入編輯畫面');
        router.push({ name: 'AnniversaryGiftModuleList' });
        return;
      }

      loading.value = true;
      try {
        const response = await autoSendService.getAnniversaryGiftModule(moduleId);
        
        if (response && response.success && response.data) {
          applyConfigToForm(response.data);
          currentModuleId.value = response.data.id;
        }
      } catch (error) {
        console.error('[AnniversaryGiftConfig] 載入設定失敗:', error);
        showError('載入設定失敗', error.message);
      } finally {
        loading.value = false;
      }
    };

    const goBackToList = () => {
      router.push({ name: 'AnniversaryGiftModuleList' });
    };

    const validateForm = () => {
      errors.moduleName = '';
      errors.activeStartAtLocal = '';
      errors.dailySendTime = '';
      errors.sendTiming = '';
      errors.tierConfigsCoverage = '';
      errors.notificationChannels = '';
      Object.keys(errors).forEach(key => {
        if (key.startsWith('tierConfigs.')) {
          delete errors[key];
        }
      });

      if (!form.moduleName || !form.moduleName.trim()) {
        errors.moduleName = '請輸入模組名稱';
        return false;
      }

      if (!form.activeStartAtLocal) {
        errors.activeStartAtLocal = '請設定啟用開始日期';
        return false;
      }

      if (form.activeEndAtLocal && form.activeEndAtLocal <= form.activeStartAtLocal) {
        errors.activeStartAtLocal = '結束日期必須晚於開始日期';
        return false;
      }

      if (!form.dailySendTime) {
        errors.dailySendTime = '請設定每日發送時間';
        return false;
      }

      if (!form.sendTiming) {
        errors.sendTiming = '請選擇發送時機';
        return false;
      }

      // 如果使用依等級設定，驗證每個 tierConfig
      if (form.settingMode === 'tier_based') {
        // 檢查是否有未設定的等級
        if (unconfiguredTiers.value.length > 0) {
          const names = unconfiguredTiers.value.map(t => t.tierName).join('、');
          errors.tierConfigsCoverage = `以下會員等級尚未設定：${names}`;
          return false;
        }
        errors.tierConfigsCoverage = '';

        let isValid = true;
        form.tierConfigs.forEach((config, index) => {
          if (!config.memberTier) {
            errors[`tierConfigs.${index}.memberTier`] = '請選擇會員等級';
            isValid = false;
          }
          
          // 檢查是否有重複的等級
          const duplicateIndex = form.tierConfigs.findIndex((c, i) => 
            i !== index && c.memberTier === config.memberTier && config.memberTier
          );
          if (duplicateIndex !== -1) {
            errors[`tierConfigs.${index}.memberTier`] = '會員等級不能重複';
            isValid = false;
          }

          if (config.sendPoints && (!config.pointsAmount || config.pointsAmount <= 0)) {
            errors[`tierConfigs.${index}.pointsAmount`] = '點數數量必須大於 0';
            isValid = false;
          }
          if (config.sendCoupons && (!config.couponTemplateIds || config.couponTemplateIds.length === 0)) {
            errors[`tierConfigs.${index}.couponTemplateIds`] = '請至少選擇一個優惠券模板';
            isValid = false;
          }
          if (isWixtarClient.value && config.sendWixtarTickets && (!config.wixtarTicketIds || config.wixtarTicketIds.length === 0)) {
            errors[`tierConfigs.${index}.wixtarTicketIds`] = '請至少選擇一張 Wixtar 票券';
            isValid = false;
          }
        });
        return isValid;
      } else {
        // 統一設定模式
        if (form.sendPoints && (!form.pointsAmount || form.pointsAmount <= 0)) {
          showError('驗證失敗', '點數數量必須大於 0');
          return false;
        }
        if (form.sendCoupons && (!form.couponTemplateIds || form.couponTemplateIds.length === 0)) {
          showError('驗證失敗', '請至少選擇一個優惠券模板');
          return false;
        }
        if (isWixtarClient.value && form.sendWixtarTickets && (!form.wixtarTicketIds || form.wixtarTicketIds.length === 0)) {
          showError('驗證失敗', '請至少選擇一張 Wixtar 票券');
          return false;
        }
      }

      // 未選擇自訂範本時，發送端會使用系統預設範本
      return true;
    };

    const handleSubmit = async () => {
      if (!validateForm()) {
        const firstError = Object.entries(errors).find(([, value]) => value);
        if (firstError) {
          const [key, message] = firstError;
          if (key.startsWith('tierConfigs') || key === 'tierConfigsCoverage') {
            activeTab.value = 'rewards';
          } else if (key === 'notificationChannels') {
            activeTab.value = 'notification';
          } else {
            activeTab.value = 'basic';
          }
          showError('驗證失敗', message);
        }
        return;
      }

      submitting.value = true;
      try {
        // 處理 daysBefore
        let daysBeforeValue = null;
        if (form.sendTiming === 'days_before') {
          daysBeforeValue = form.daysBefore || null;
        }

        // 處理 couponTemplateIds（統一設定模式）
        let couponTemplateIdsValue = [];
        if (form.settingMode === 'unified' && form.sendCoupons && form.couponTemplateIds && Array.isArray(form.couponTemplateIds) && form.couponTemplateIds.length > 0) {
          couponTemplateIdsValue = form.couponTemplateIds;
        }

        // 處理 tierConfigs（依等級設定模式）
        let tierConfigsValue = null;
        if (form.settingMode === 'tier_based' && form.tierConfigs && form.tierConfigs.length > 0) {
          tierConfigsValue = form.tierConfigs
            .filter(config => config.memberTier) // 過濾掉沒有選擇等級的設定
            .map(config => ({
              memberTier: config.memberTier,
              sendPoints: config.sendPoints || false,
              pointsAmount: config.sendPoints ? (config.pointsAmount || 0) : null,
              pointsExpiryDays: null,
              sendCoupons: config.sendCoupons || false,
              couponTemplateIds: config.sendCoupons && config.couponTemplateIds && config.couponTemplateIds.length > 0
                ? config.couponTemplateIds
                : [],
              sendWixtarTickets: memberSystemLoaded.value && !isWixtarClient.value
                ? false
                : (config.sendWixtarTickets || false),
              wixtarTicketIds: memberSystemLoaded.value && !isWixtarClient.value
                ? []
                : (config.sendWixtarTickets && config.wixtarTicketIds && config.wixtarTicketIds.length > 0
                  ? config.wixtarTicketIds
                  : [])
            }));
          
          // 如果過濾後為空陣列，設為 null（表示不使用依等級設定）
          if (tierConfigsValue.length === 0) {
            tierConfigsValue = null;
          }
        }

        const data = {
          moduleName: form.moduleName.trim(),
          activeStartAt: fromDateInputValue(form.activeStartAtLocal),
          activeEndAt: fromDateInputValue(form.activeEndAtLocal, true),
          isEnabled: form.isEnabled,
          dailySendTime: formatTimeForApi(form.dailySendTime),
          sendTiming: form.sendTiming,
          daysBefore: daysBeforeValue,
          sendPoints: form.settingMode === 'unified' ? form.sendPoints : false,
          pointsAmount: form.settingMode === 'unified' && form.sendPoints ? form.pointsAmount : null,
          pointsExpiryDays: null,
          sendCoupons: form.settingMode === 'unified' ? form.sendCoupons : false,
          couponTemplateIds: couponTemplateIdsValue,
          sendWixtarTickets: memberSystemLoaded.value && !isWixtarClient.value
            ? false
            : (form.settingMode === 'unified' ? form.sendWixtarTickets : false),
          wixtarTicketIds: memberSystemLoaded.value && !isWixtarClient.value
            ? []
            : (form.settingMode === 'unified' && form.sendWixtarTickets ? form.wixtarTicketIds : []),
          tierConfigs: tierConfigsValue,
          sendNotification: form.notificationChannels && form.notificationChannels.length > 0,
          notificationChannel: form.notificationChannels && form.notificationChannels.length > 0
            ? form.notificationChannels[0] : null,
          notificationChannels: form.notificationChannels || [],
          notificationTemplateIds: form.notificationTemplateIds || {},
          lineConfigIds: form.lineConfigIds || []
        };

        const response = await autoSendService.updateAnniversaryGiftModule(currentModuleId.value, data);

        if (response && response.success) {
          await showSuccess('設定儲存成功', '週年禮設定已更新');
          await loadConfig(currentModuleId.value);
          await loadStatistics();
          await loadLogs();
        } else {
          showError('儲存設定失敗', response?.message || '未知錯誤');
        }
      } catch (error) {
        showError('儲存設定失敗', error.message || '請檢查輸入資料');
      } finally {
        submitting.value = false;
      }
    };

    // 載入統計資料
    const loadStatistics = async () => {
      statisticsLoading.value = true;
      try {
        const response = await autoSendService.getAnniversaryGiftLogs({
          page: 1,
          pageSize: 1000 // 取得足夠的資料來計算統計
        });
        
        if (response && response.success && response.data) {
          const items = response.data.items || [];
          const total = items.length;
          const success = items.filter(item => item.status === 'success').length;
          const failed = items.filter(item => item.status === 'failed').length;
          const successRate = total > 0 ? Math.round((success / total) * 100) : 0;
          
          statistics.value = {
            totalSent: total,
            successSent: success,
            failedSent: failed,
            successRate
          };
          
          // 計算趨勢資料（近30天）
          const now = new Date();
          const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          const dailyCounts = {};
          
          items.forEach(item => {
            const date = new Date(item.sentAt || item.createdAt);
            if (date >= thirtyDaysAgo) {
              const dateStr = date.toISOString().split('T')[0];
              dailyCounts[dateStr] = (dailyCounts[dateStr] || 0) + 1;
            }
          });
          
          // 生成趨勢資料陣列
          trendData.value = [];
          for (let i = 29; i >= 0; i--) {
            const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
            const dateStr = date.toISOString().split('T')[0];
            trendData.value.push({
              date: dateStr,
              count: dailyCounts[dateStr] || 0
            });
          }
        }
      } catch (error) {
        console.error('載入統計資料失敗:', error);
      } finally {
        statisticsLoading.value = false;
      }
    };

    // 載入發送記錄
    const loadLogs = async () => {
      logsLoading.value = true;
      try {
        const params = {
          page: logsPagination.page,
          pageSize: logsPagination.pageSize
        };
        
        if (logsFilter.status) {
          params.status = logsFilter.status;
        }
        
        if (logsFilter.memberName) {
          params.memberName = logsFilter.memberName;
        }

        if (logsFilter.startDate) {
          params.startDate = logsFilter.startDate;
        }

        if (logsFilter.endDate) {
          params.endDate = logsFilter.endDate;
        }
        
        const response = await autoSendService.getAnniversaryGiftLogs(params);
        
        if (response && response.success && response.data) {
          logs.value = response.data.items || [];
          if (response.data.pagination) {
            Object.assign(logsPagination, {
              total: response.data.pagination.totalItems || 0,
              totalPages: response.data.pagination.totalPages || 0
            });
          }
        }
      } catch (error) {
        console.error('載入發送記錄失敗:', error);
        showError('載入發送記錄失敗', error.message);
      } finally {
        logsLoading.value = false;
      }
    };

    // 處理記錄搜尋（防抖）
    const handleLogsSearch = () => {
      clearTimeout(logsSearchTimeout.value);
      logsSearchTimeout.value = setTimeout(() => {
        logsPagination.page = 1;
        loadLogs();
      }, 500);
    };

    const searchLogs = () => {
      logsPagination.page = 1;
      logsSearched.value = true;
      loadStatistics();
      loadLogs();
    };

    const resetLogsFilter = () => {
      logsFilter.status = '';
      logsFilter.memberName = '';
      logsFilter.startDate = '';
      logsFilter.endDate = '';
      logsPagination.page = 1;
      logsSearched.value = false;
      logs.value = [];
    };

    // 切換記錄頁面
    const changeLogsPage = (page) => {
      logsPagination.page = page;
      loadLogs();
    };

    // 格式化日期（僅年月日）
    const formatDate = (dateString) => {
      if (!dateString) return '-';
      return String(dateString).slice(0, 10);
    };

    // 格式化日期時間
    const formatDateTime = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    // 取得狀態標籤樣式
    const getStatusBadgeClass = (status) => {
      const classes = {
        'success': 'badge badge-success',
        'failed': 'badge badge-danger',
        'pending': 'badge badge-warning'
      };
      return classes[status] || 'badge badge-secondary';
    };

    // 取得狀態文字
    const getStatusText = (status) => {
      const texts = {
        'success': '成功',
        'failed': '失敗',
        'pending': '處理中'
      };
      return texts[status] || status || '-';
    };

    const handleReset = () => {
      loadConfig();
    };

    const loadLineConfigs = async () => {
      lineConfigsLoading.value = true;
      try {
        const response = await lineConfigService.list({ pageSize: 100 });
        if (response && response.success && response.data) {
          lineConfigs.value = response.data.items || response.data || [];
        }
      } catch (error) {
        console.error('[AnniversaryGiftConfig] 載入 LINE 帳號失敗:', error);
      } finally {
        lineConfigsLoading.value = false;
      }
    };

    watch(
      () => route.params.moduleId,
      async (moduleId) => {
        if (moduleId && moduleId !== currentModuleId.value) {
          currentModuleId.value = moduleId;
          await loadConfig(moduleId);
          await loadStatistics();
          await loadLogs();
        }
      }
    );

    const setUnifiedWixtarTicketIds = (ids) => {
      form.wixtarTicketIds = ids;
      if (ids.length > 0) form.sendWixtarTickets = true;
    };

    const setTierWixtarTicketIds = (index, ids) => {
      const tierConfig = form.tierConfigs[index];
      if (!tierConfig) return;
      tierConfig.wixtarTicketIds = ids;
      if (ids.length > 0) tierConfig.sendWixtarTickets = true;
    };

    const loadClientMemberSystem = async () => {
      const user = authService.getCurrentUser();
      if (!user?.clientId) {
        isWixtarClient.value = false;
        memberSystemLoaded.value = true;
        return;
      }
      try {
        const res = await clientService.getClient(user.clientId);
        isWixtarClient.value = res?.data?.clientData?.memberSystemType === 'WIXTAR';
      } catch (error) {
        console.error('載入會員模式失敗:', error);
        isWixtarClient.value = false;
        memberSystemLoaded.value = false;
        return;
      }
      memberSystemLoaded.value = true;
    };

    const loadWixtarTickets = async () => {
      if (!isWixtarClient.value) {
        wixtarTickets.value = [];
        return;
      }
      wixtarTicketsLoading.value = true;
      try {
        const all = [];
        let page = 1;
        let totalPages = 1;
        do {
          const response = await wixtarService.getTicketCatalog({
            page,
            pageSize: 100,
            sortBy: 'activity',
            sortOrder: 'asc'
          });
          const items = response?.data?.items || [];
          all.push(...items);
          totalPages = response?.data?.pagination?.totalPages || 1;
          page += 1;
        } while (page <= totalPages && page <= 10);
        wixtarTickets.value = all;
      } catch (error) {
        console.error('載入 Wixtar 票券失敗:', error);
        wixtarTickets.value = [];
      } finally {
        wixtarTicketsLoading.value = false;
      }
    };

    onMounted(async () => {
      if (!currentModuleId.value) {
        showError('缺少模組 ID', '請從模組清單進入編輯畫面');
        router.push({ name: 'AnniversaryGiftModuleList' });
        return;
      }

      await loadClientMemberSystem();
      await Promise.all([
        loadTemplates(),
        loadTiers(),
        loadLineConfigs(),
        loadWixtarTickets()
      ]);

      await loadConfig(currentModuleId.value);
      await loadStatistics();
      await loadLogs();
    });

    return {
      pageTitle,
      loading,
      goBackToList,
      form,
      errors,
      submitting,
      templates,
      templatesLoading,
      templateSearchQuery,
      filteredTemplates,
      selectedTemplates,
      isTemplateSelected,
      toggleTemplate,
      removeTemplate,
      handleTemplateSearch,
      getCouponTypeText,
      formatDiscount,
      tiers,
      availableTiers,
      getAvailableTiersForIndex,
      unconfiguredTiers,
      getTierName,
      getFilteredTemplatesForTier,
      getSelectedTierTemplates,
      isTierTemplateSelected,
      toggleTierTemplate,
      removeTierTemplate,
      addTierConfig,
      removeTierConfig,
      handleTierChange,
      getTierSearchQuery,
      setTierSearchQuery,
      tierTemplateSearchQueries,
      statistics,
      statisticsLoading,
      trendData,
      logs,
      logsLoading,
      logsFilter,
      logsSearched,
      logsPagination,
      handleLogsSearch,
      searchLogs,
      resetLogsFilter,
      changeLogsPage,
      formatDate,
      formatDateTime,
      getStatusBadgeClass,
      getStatusText,
      handleSubmit,
      handleReset,
      expandedLogId,
      toggleLogExpand,
      manualSendMembers,
      manualSending,
      manualSendMemberSearch,
      handleManualSend,
      channelIcon,
      channelLabel,
      getNotifTitle,
      activeTab,
      lineConfigs,
      lineConfigsLoading,
      isWixtarClient,
      wixtarTickets,
      wixtarTicketsLoading,
      logsColspan,
      setUnifiedWixtarTicketIds,
      setTierWixtarTicketIds
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

/* ===== 手動補發 ===== */
.manual-send-panel {
  padding: 16px;
  margin-bottom: 20px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
}

.manual-send-actions {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}

/* ===== LINE 官方帳號選擇 ===== */
.section-desc {
  font-size: 13px;
  color: #6c757d;
  margin-bottom: 12px;
}

.line-config-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.line-config-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  user-select: none;
}

.line-config-item:hover {
  background: #f0f9ff;
  border-color: #3b82f6;
}

.line-config-checkbox {
  width: 16px;
  height: 16px;
  accent-color: #06c755;
  cursor: pointer;
  flex-shrink: 0;
}

.line-config-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.line-config-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.line-config-avatar.placeholder {
  background: #06c755;
  color: #fff;
  font-size: 18px;
}

.line-config-name {
  font-size: 14px;
  font-weight: 500;
  flex: 1;
}

.line-config-id {
  font-size: 12px;
}

.line-multi-warning {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  line-height: 1.5;
}

.line-multi-warning i {
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 1px;
}

/* ===== 設定模式切換 ===== */
.mode-toggle-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.mode-option {
  font-size: 14px;
  font-weight: 500;
  color: #9ca3af;
  transition: color 0.2s;
}

.mode-option.active {
  color: #1976d2;
}

.mode-desc {
  margin-top: 8px;
  font-size: 13px;
  color: #6c757d;
}

/* ===== Toggle 開關 ===== */
.toggle-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle-label {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #ccc;
  border-radius: 24px;
  transition: background 0.2s;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  top: 3px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.toggle-switch input:checked + .toggle-slider {
  background: #4caf50;
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(20px);
}

.toggle-status {
  font-size: 13px;
  font-weight: 500;
}

.toggle-status.on {
  color: #4caf50;
}

.toggle-status.off {
  color: #9ca3af;
}

/* 發送紀錄篩選：動作按鈕組橫向排列 */
.page-toolbar .filter-group--action {
  display: flex;
  align-items: center;
  width: auto;
  gap: 6px;
}

/* ===== 發送紀錄空狀態 ===== */
.logs-empty-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #9ca3af;
  gap: 10px;
}

.logs-empty-hint i {
  font-size: 40px;
  color: #d1d5db;
}

.logs-empty-hint p {
  font-size: 14px;
  margin: 0;
}

/* ===== 等級覆蓋警示 ===== */
.tier-coverage-warning {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 13px;
  color: #92400e;
}

.tier-coverage-warning i {
  font-size: 18px;
  color: #f59e0b;
  flex-shrink: 0;
  margin-top: 1px;
}

.missing-tier-tag {
  display: inline-block;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 4px;
  padding: 1px 8px;
  margin: 0 4px 4px 0;
  font-weight: 600;
}

.warning-tip {
  display: block;
  margin-top: 4px;
  color: #b45309;
  font-size: 12px;
}

.tier-coverage-ok {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 13px;
  color: #166534;
}

.tier-coverage-ok i {
  font-size: 16px;
  color: #22c55e;
}

/* ===== 整點時間格 ===== */
.hour-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 6px;
}

.hour-btn {
  padding: 8px 4px;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  background: #f9fafb;
  border: 1.5px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s, color 0.12s;
  text-align: center;
}

.hour-btn:hover {
  border-color: #93c5fd;
  background: #f0f9ff;
  color: #1d4ed8;
}

.hour-btn.active {
  border-color: #3b82f6;
  background: #3b82f6;
  color: #fff;
  font-weight: 600;
}

/* ===== 發送時機卡片 ===== */
.timing-cards {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.timing-card {
  flex: 1;
  min-width: 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 16px 12px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  text-align: center;
}

.timing-card input[type='radio'] {
  display: none;
}

.timing-card:hover {
  border-color: #93c5fd;
  background: #f0f9ff;
}

.timing-card.active {
  border-color: #3b82f6;
  background: #eff6ff;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
}

.timing-icon {
  font-size: 26px;
  color: #9ca3af;
  transition: color 0.15s;
}

.timing-card.active .timing-icon {
  color: #3b82f6;
}

.timing-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.timing-desc {
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.4;
}

.days-before-input {
  display: flex;
  align-items: center;
  gap: 10px;
}

.days-prefix,
.days-suffix {
  font-size: 14px;
  color: #374151;
  white-space: nowrap;
}

/* ===== Tab 導航 ===== */
.tab-nav {
  display: flex;
  gap: 4px;
  margin-bottom: 20px;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0;
}

.tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  border-radius: 6px 6px 0 0;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
}

.tab-btn:hover {
  color: #374151;
  background: #f3f4f6;
}

.tab-btn.active {
  color: #3b82f6;
  border-bottom-color: #3b82f6;
  background: #eff6ff;
}

.form-section {
  margin-bottom: 30px;
}


.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.required {
  color: #dc3545;
}

.error-text {
  display: block;
  color: #dc3545;
  font-size: 14px;
  margin-top: 5px;
}

.form-text {
  display: block;
  margin-top: 5px;
  color: #6c757d;
  font-size: 14px;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 優惠券模板選擇器樣式 */
.loading-templates {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 20px;
  text-align: center;
  color: #666;
}

.template-selector {
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
}

.template-search {
  padding: 12px;
  border-bottom: 1px solid #eee;
}

.template-search input {
  width: 100%;
}

.template-list {
  max-height: 300px;
  overflow-y: auto;
  padding: 8px;
}

.template-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.template-item:hover {
  background: #f5f5f5;
  border-color: #1976d2;
}

.template-item.selected {
  background: #e3f2fd;
  border-color: #1976d2;
}

.template-item input[type="checkbox"] {
  margin-top: 2px;
  cursor: pointer;
}

.template-info {
  flex: 1;
}

.template-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.template-details {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 12px;
  color: #666;
}

.template-type {
  padding: 2px 8px;
  background: #f0f0f0;
  border-radius: 12px;
}

.template-discount {
  color: #1976d2;
  font-weight: 500;
}

.template-validity {
  color: #666;
}

.no-templates {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.no-templates i {
  font-size: 48px;
  margin-bottom: 12px;
  display: block;
}

.selected-templates {
  margin-top: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 4px;
}

.selected-label {
  font-weight: 500;
  margin-bottom: 8px;
  color: #333;
}

.selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.selected-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #1976d2;
  color: white;
  border-radius: 16px;
  font-size: 14px;
}

.remove-tag {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  font-size: 16px;
  line-height: 1;
}

.remove-tag:hover {
  opacity: 0.8;
}

/* 設定模式選擇 */
.radio-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.radio-label input[type="radio"] {
  cursor: pointer;
}

/* 等級設定卡片 */
.tier-config-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 20px;
  background: #fff;
}

.tier-config-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  border-radius: 8px 8px 0 0;
}

.tier-config-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #333;
}

.tier-config-body {
  padding: 20px;
}

/* 統計卡片 */
.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
}

.stat-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 32px;
  font-weight: 600;
  color: #333;
}

/* 圖表區塊 */
.chart-section {
  margin-bottom: 30px;
}

.chart-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
}

.section-subtitle {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #333;
}

/* 表格區塊 */
.table-section {
  margin-top: 30px;
}

.table-wrapper {
  overflow-x: auto;
  margin-bottom: 20px;
}

.table-wrapper table {
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
}

.table-wrapper th,
.table-wrapper td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

.table-wrapper th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
}

.table-wrapper tbody tr:hover {
  background: #f8f9fa;
}

.text-center {
  text-align: center;
}

.text-muted {
  color: #999;
}

.badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.badge-success {
  background: #d4edda;
  color: #155724;
}

.badge-danger {
  background: #f8d7da;
  color: #721c24;
}

.badge-warning {
  background: #fff3cd;
  color: #856404;
}

.badge-secondary {
  background: #e2e3e5;
  color: #383d41;
}

/* 分頁 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
}

.page-info {
  color: #666;
  font-size: 14px;
}

/* 日誌表格增強 */
.log-row {
  cursor: pointer;
}

.log-row:hover {
  background: #f0f9ff;
}

.log-row.expanded {
  background: #eff6ff;
}

.log-detail-row td {
  padding: 0 !important;
}

.log-detail {
  padding: 16px 20px;
  background: #f8faff;
  border-top: 1px solid #dbeafe;
}

.log-detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
  font-size: 13px;
}

.log-detail-grid strong {
  display: block;
  margin-bottom: 6px;
  color: #374151;
}

/* 通知管道圖示 */
.notification-icons {
  display: flex;
  gap: 4px;
  align-items: center;
}

.notif-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 14px;
}

.notif-icon.sent {
  background: #d1fae5;
  color: #059669;
}

.notif-icon.failed {
  background: #fee2e2;
  color: #dc2626;
}

.notif-result-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  font-size: 13px;
}

.ch-label {
  min-width: 40px;
  color: #6b7280;
}

.result-ok {
  color: #059669;
  display: flex;
  align-items: center;
  gap: 4px;
}

.result-fail {
  color: #dc2626;
  display: flex;
  align-items: center;
  gap: 4px;
}

.result-ok small,
.result-fail small {
  color: #9ca3af;
  font-size: 11px;
}

.error-msg {
  color: #dc2626;
  font-size: 13px;
  margin-top: 4px;
  word-break: break-all;
}

.error-code {
  color: #9ca3af;
  font-size: 11px;
  margin-top: 2px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

@media (max-width: 768px) {
  .header-actions {
    width: 100%;
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .page-container {
    padding-top: 96px;
  }
}
</style>
